import * as React from "react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { isDev, isTest, featureFlags } from "config";
import { ApiError } from "utils/api";

// Mutable callback reference for auth errors - gets set from inside the React tree
// This pattern is recommended by TkDodo for handling global errors when the callback
// needs access to React context values (like signOut)
let authErrorCallback: (() => void) | null = null;

const setAuthErrorCallback = (callback: (() => void) | null) => {
  authErrorCallback = callback;
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      // Handle auth token errors globally - fires once per query, not per component
      if (
        error instanceof Error &&
        error.message.includes("Authorization token is invalid")
      ) {
        authErrorCallback?.();
      }
    },
  }),
  defaultOptions: {
    queries: {
      // React-Query retries failed network requests with exponentially growing delays. This
      // customizes that behavior to prevent endless retries and maxes at 3. It also prevents
      // retries while testing and for client error responses (like a 401 that will continue
      // to fail until the user signs in, or a 404 for a non-existent resource).
      retry: (failures, error) => {
        const failureMax = isTest ? 0 : 3;
        if (failures >= failureMax) {
          return false;
        }
        if (error instanceof ApiError && error.status < 500) {
          return false;
        }
        return true;
      },
      // Do not refetch when window regains focus in development environment. This helps simplify
      // debugging when clicking back and forth between window and dev tools.
      refetchOnWindowFocus: !isDev,
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}

      {featureFlags.enableQueryDevTools && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
}

export { QueryProvider, setAuthErrorCallback };
